// The Drone-Assisted Warehouse (§33 I5): the store's upgrade, built over an
// own store and keeping its stock. It "instantly transports material to any
// piece or structure in a 5 square radius around itself when a factory
// process demands material not in the local inventory" — read as two doors:
//
// - **A forge's shortfall** (`warehousesFor`, the coalition's reserve): a
//   bill the foundries touching the square cannot cover is finished from the
//   nearest warehouse in reach, never shared out ahead of them.
// - **A hungry machine** (`runWarehouses`, in the tick after `runStores`):
//   each own refiner, turret or generator in reach holding nothing it can
//   work gets one unit it accepts, cheapest first.
//
// Builds and upgrades need neither: a warehouse is a stockpile, and
// `storesNearest` already pays them from every stockpile.

import { type Color, pieceColor } from "../board";
import type { Position } from "../position";
import { structureRow } from "../rules";
import { acceptsUnit, refinesAt } from "./machines";
import { bufferTotal, deliverUnit, DRONE_WAREHOUSE, type IndustryState, structureAt, unitValue } from "./state";
import { inWarehouseReach } from "./coalition";
import { isTurretKind } from "./turret";

/**
 * `side`'s machines that hold nothing to work with, in board order: refiners
 * with an empty bank, turrets with an empty magazine, coal burners with no
 * coal. A stockpile is not a process, so no store is ever topped up.
 */
function hungryMachines(position: Position, industry: IndustryState, side: Color): number[] {
  const out: number[] = [];
  for (const [sq, piece] of position.board) {
    if (pieceColor(piece) === side && refinesAt(position, sq) && bufferTotal(industry, sq, "in") === 0) out.push(sq);
  }
  for (const [sq, structure] of industry.structures) {
    if (structure.owner !== side || structure.stock.length > 0) continue;
    if (isTurretKind(structure.kind) || structureRow(structure.kind).burnsCoal) out.push(sq);
  }
  return out.sort((a, b) => a - b);
}

/** The index of the cheapest unit in `stock` the machine on `to` accepts, or -1. */
function cheapestAccepted(position: Position, industry: IndustryState, to: number, side: Color, stock: number[]): number {
  let best = -1;
  for (let i = 0; i < stock.length; i++) {
    if (best >= 0 && unitValue(stock[i]) >= unitValue(stock[best])) continue;
    if (acceptsUnit(position, industry, to, side, stock[i])) best = i;
  }
  return best;
}

/**
 * Feed `side`'s hungry machines in reach of its warehouses, one unit each, in
 * place. Warehouses in board order; a machine fed by one is not fed again.
 */
export function runWarehouses(position: Position, industry: IndustryState, side: Color): void {
  const warehouses = [...industry.structures.entries()]
    .filter(([, structure]) => structure.kind === DRONE_WAREHOUSE && structure.owner === side)
    .map(([sq]) => sq)
    .sort((a, b) => a - b);
  if (warehouses.length === 0) return;
  const fed = new Set<number>();
  const hungry = hungryMachines(position, industry, side);
  for (const from of warehouses) {
    const stock = structureAt(industry, from)!.stock;
    for (const to of hungry) {
      if (stock.length === 0) break;
      if (fed.has(to) || !inWarehouseReach(from, to)) continue;
      const pick = cheapestAccepted(position, industry, to, side, stock);
      if (pick < 0) continue;
      const [unit] = stock.splice(pick, 1);
      if (deliverUnit(position, industry, to, unit)) fed.add(to);
      else stock.splice(pick, 0, unit);
    }
  }
}
